import { Link } from 'react-router';
import { ArrowRight, BarChart3, Bot, Database, ShieldCheck } from 'lucide-react';
import { APP_NAME } from '@/utils/constants';

export function LandingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/40">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-bold">
            {APP_NAME.charAt(0).toUpperCase()}
          </div>
          <span className="text-lg font-semibold">{APP_NAME}</span>
        </div>
        <nav className="flex items-center gap-3">
          <Link
            to="/login"
            className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90"
          >
            Get started
          </Link>
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-16">
        <section className="mx-auto max-w-3xl text-center">
          <h1 className="text-balance text-4xl font-bold tracking-tight sm:text-5xl">
            The AI analytics layer for your ERP
          </h1>
          <p className="mt-5 text-balance text-lg text-muted-foreground">
            Plug any ERP into {APP_NAME} and get instant dashboards, insights, and natural-language
            queries on top of your data — without an army of analysts.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3">
            <Link
              to="/signup"
              className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Start free <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="#how"
              className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
            >
              How it works
            </a>
          </div>
        </section>

        <section id="how" className="mt-20 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Feature
            icon={Database}
            title="Connect anything"
            body="Native connectors for Odoo, SAP, NetSuite, and Dynamics. Generic REST + CSV for everything else."
          />
          <Feature
            icon={Bot}
            title="AI dashboards"
            body="Choose your AI provider (Gemini, Groq, OpenRouter) and stream insights right into your reports."
          />
          <Feature
            icon={BarChart3}
            title="Natural-language queries"
            body='Ask "Top customers last quarter?" — get an answer and a chart, in seconds.'
          />
          <Feature
            icon={ShieldCheck}
            title="Multi-tenant & secure"
            body="Per-org isolation enforced at both the application and Postgres RLS layers. SSO-ready."
          />
        </section>

        <section className="mt-20 rounded-2xl border border-border bg-card p-10 text-center">
          <h2 className="text-2xl font-semibold">Ready to plug in?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Create your org, connect a data source, and see your first AI-driven dashboard in under
            five minutes.
          </p>
          <Link
            to="/signup"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Create my org <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </main>

      <footer className="mx-auto max-w-6xl px-6 py-10 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} {APP_NAME}.
      </footer>
    </div>
  );
}

interface FeatureProps {
  icon: typeof Database;
  title: string;
  body: string;
}

function Feature({ icon: Icon, title, body }: FeatureProps) {
  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <Icon className="h-6 w-6 text-primary" />
      <h3 className="mt-3 font-semibold">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
